import React, { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MainDashboard } from './components/MainDashboard';
import { ClientDashboard } from './components/ClientDashboard';
import { BannerSetEditor } from './components/BannerSetEditor';
import { BannerEditor } from './components/BannerEditor';
import { UserManagement } from './components/UserManagement';
import { LoginPage } from './components/LoginPage';
import { UserProfile } from './components/UserProfile';
import { ToastContainer } from './components/ToastContainer';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Client, BannerSet, BannerSize } from './types';
import { SAMPLE_CLIENTS } from './constants/sampleData';
import { Users, Settings, User, LogOut, Shield } from 'lucide-react';
import { dataService } from './services/dataService';
import { DebugPanel } from './components/DebugPanel';

type ViewState = 
  | { type: 'main' }
  | { type: 'client'; clientId: string }
  | { type: 'bannerSet'; clientId: string; bannerSetId: string }
  | { type: 'bannerEditor'; clientId: string; bannerSetId: string; sizeId: string }
  | { type: 'users' };

const AppContent: React.FC = () => {
  const { user, isAuthenticated, isLoading, hasPermission, canAccessClient } = useAuth();
  const [clients, setClients] = useState<Client[]>(SAMPLE_CLIENTS);
  const [currentView, setCurrentView] = useState<ViewState>({ type: 'main' });
  const [showUserProfile, setShowUserProfile] = useState(false);

  // Filter clients based on user permissions
  const getAccessibleClients = () => {
    if (!user) return [];
    
    if (user.role === 'super_admin' || user.role === 'admin') {
      return clients;
    }
    
    if (user.clientId) {
      return clients.filter(client => client.id === user.clientId);
    }
    
    return [];
  };

  const navigateToClient = (clientId: string) => {
    if (canAccessClient(clientId)) {
      setCurrentView({ type: 'client', clientId });
    }
  };

  const navigateToBannerSet = (clientId: string, bannerSetId: string) => {
    if (canAccessClient(clientId)) {
      setCurrentView({ type: 'bannerSet', clientId, bannerSetId });
    }
  };

  const navigateToBannerEditor = (clientId: string, bannerSetId: string, sizeId: string) => {
    if (canAccessClient(clientId)) {
      setCurrentView({ type: 'bannerEditor', clientId, bannerSetId, sizeId });
    }
  };

  const navigateToUsers = () => {
    if (hasPermission('users', 'read')) {
      setCurrentView({ type: 'users' });
    }
  };

  const navigateBack = () => {
    switch (currentView.type) {
      case 'client':
        setCurrentView({ type: 'main' });
        break;
      case 'bannerSet':
        setCurrentView({ type: 'client', clientId: currentView.clientId });
        break;
      case 'bannerEditor':
        setCurrentView({ type: 'bannerSet', clientId: currentView.clientId, bannerSetId: currentView.bannerSetId });
        break;
      case 'users':
        setCurrentView({ type: 'main' });
        break;
    }
  };

  const updateClient = (clientId: string, updates: Partial<Client>) => {
    if (canAccessClient(clientId)) {
      setClients(prev => prev.map(client => 
        client.id === clientId ? { ...client, ...updates } : client
      ));
    }
  };

  const addClient = (newClient: Client) => {
    if (hasPermission('clients', 'create')) {
      setClients(prev => [...prev, newClient]);
    }
  };

  const deleteClient = (clientId: string) => {
    if (hasPermission('clients', 'delete')) {
      setClients(prev => prev.filter(client => client.id !== clientId));
    }
  };

  const updateBannerSet = async (clientId: string, bannerSetId: string, updates: Partial<BannerSet>) => {
    if (canAccessClient(clientId)) {
      try {
        // Update backend first
        await dataService.updateBannerSet(bannerSetId, updates);
        
        // Then update local state
        setClients(prev => prev.map(client => 
          client.id === clientId 
            ? {
                ...client,
                bannerSets: client.bannerSets.map(set =>
                  set.id === bannerSetId ? { ...set, ...updates } : set
                )
              }
            : client
        ));
      } catch (error) {
        console.error('Failed to update banner set:', error);
        throw error;
      }
    }
  };

  const getCurrentClient = () => {
    if (currentView.type === 'main' || currentView.type === 'users') return null;
    return clients.find(c => c.id === currentView.clientId) || null;
  };

  const getCurrentBannerSet = () => {
    if (currentView.type === 'main' || currentView.type === 'client' || currentView.type === 'users') return null;
    const client = getCurrentClient();
    if (!client) return null;
    return client.bannerSets.find(set => set.id === currentView.bannerSetId) || null;
  };

  const getCurrentSize = () => {
    if (currentView.type !== 'bannerEditor') return null;
    const bannerSet = getCurrentBannerSet();
    if (!bannerSet) return null;
    return bannerSet.sizes.find(size => size.id === currentView.sizeId) || null;
  };

  const getPageTitle = () => {
    switch (currentView.type) {
      case 'main': return 'Client Dashboard';
      case 'client': return `${getCurrentClient()?.name} - Banner Sets`;
      case 'bannerSet': return `${getCurrentClient()?.name} - ${getCurrentBannerSet()?.name}`;
      case 'bannerEditor': return `${getCurrentClient()?.name} - ${getCurrentBannerSet()?.name} - ${getCurrentSize()?.name}`;
      case 'users': return 'User Management';
      default: return 'DealerCentives';
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">DC</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">DealerCentives</h1>
                  <p className="text-xs text-gray-500">{getPageTitle()}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Navigation */}
              {currentView.type !== 'main' && currentView.type !== 'users' && (
                <nav className="flex items-center space-x-2 text-sm">
                  <button
                    onClick={() => setCurrentView({ type: 'main' })}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Dashboard
                  </button>
                  {currentView.type !== 'client' && (
                    <>
                      <span className="text-gray-400">/</span>
                      <button
                        onClick={() => setCurrentView({ type: 'client', clientId: currentView.clientId })}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {getCurrentClient()?.name}
                      </button>
                    </>
                  )}
                  {currentView.type === 'bannerSet' && (
                    <>
                      <span className="text-gray-400">/</span>
                      <span className="text-gray-600">{getCurrentBannerSet()?.name}</span>
                    </>
                  )}
                  {currentView.type === 'bannerEditor' && (
                    <>
                      <span className="text-gray-400">/</span>
                      <button
                        onClick={() => setCurrentView({ type: 'bannerSet', clientId: currentView.clientId, bannerSetId: currentView.bannerSetId })}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {getCurrentBannerSet()?.name}
                      </button>
                      <span className="text-gray-400">/</span>
                      <span className="text-gray-600">{getCurrentSize()?.name}</span>
                    </>
                  )}
                </nav>
              )}

              {/* Admin Navigation */}
              {hasPermission('users', 'read') && (
                <button
                  onClick={navigateToUsers}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    currentView.type === 'users'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Users size={16} />
                  <span className="text-sm">Users</span>
                </button>
              )}

              {/* User Menu */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">
                      {user?.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowUserProfile(true)}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                        <User size={16} className="text-gray-600" />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {currentView.type === 'main' && (
            <MainDashboard
              clients={getAccessibleClients()}
              onNavigateToClient={navigateToClient}
              onUpdateClient={updateClient}
              onAddClient={addClient}
              onDeleteClient={deleteClient}
            />
          )}

          {currentView.type === 'client' && (
            <ClientDashboard
              client={getCurrentClient()!}
              onNavigateToBannerSet={navigateToBannerSet}
              onUpdateClient={(updates) => updateClient(currentView.clientId, updates)}
              onBack={navigateBack}
            />
          )}

          {currentView.type === 'bannerSet' && (
            <BannerSetEditor
              client={getCurrentClient()!}
              bannerSet={getCurrentBannerSet()!}
              onNavigateToBannerEditor={navigateToBannerEditor}
              onUpdateBannerSet={(updates) => updateBannerSet(currentView.clientId, currentView.bannerSetId, updates)}
              onBack={navigateBack}
            />
          )}

          {currentView.type === 'bannerEditor' && (
            <BannerEditor
              client={getCurrentClient()!}
              bannerSet={getCurrentBannerSet()!}
              size={getCurrentSize()!}
              onUpdateBannerSet={(updates) => updateBannerSet(currentView.clientId, currentView.bannerSetId, updates)}
              onBack={navigateBack}
            />
          )}

          {currentView.type === 'users' && <UserManagement />}
        </div>

        {/* User Profile Modal */}
        <UserProfile
          isOpen={showUserProfile}
          onClose={() => setShowUserProfile(false)}
        />

        {/* Toast Notifications */}
        <ToastContainer />
      </div>
      <DebugPanel />
    </DndProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;